import { Controller } from "@hotwired/stimulus"

import {
  DraftStore,
  readScreen,
  restoreAnswers,
  serializeAnswers,
  writeScreen,
} from "../../lib/inspections/draft_store"
import { PageSaveQueue } from "../../lib/inspections/page_save_queue"

const DRAFT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000

/**
 * Inspections question pager (CONEK-4653, spec §6.3; swipe deck CONEK-8802).
 *
 * Mounted on the pager root, around the progress bars, the save status and the
 * pages. Every page is a `slide` target holding its own answers `form`.
 *
 * Classic mode (the swipe pager flag off, `deck` false): the root holds the
 * requested page alone. Back / Next / Review and the progress bar's steps
 * submit the page form to Inspections::SavePage and Turbo follows the
 * server's redirect to the adjacent page. This controller only sets the
 * hidden `direction` field of the clicked button's form and marks the buttons
 * busy while the request runs.
 *
 * Deck mode (`deck` true): every page of the inspection is a slide of a
 * Ui::SwipeDeckComponent. Nothing navigates while moving between pages:
 *   - a swipe, Back / Next, or a progress step moves the deck;
 *   - the page left is saved in the background (PageSaveQueue,
 *     `direction=stay`) and marked completed, exactly as Next / Back always
 *     have; its answers stay in the document whatever the network does;
 *   - the progress bar shown follows the deck (one bar per page is stacked,
 *     and a save replaces the stack, recoloured, through a Turbo Stream);
 *   - the URL and the document title follow the page, so a reload lands
 *     where the inspector was;
 *   - Review (the button, the last step, or a swipe past the last page)
 *     waits for every queued save, then submits the page normally so the
 *     server saves it and redirects to the review.
 * The page being edited is also saved, without completing it, when the
 * document is hidden or left (`keepalive`), so leaving the pager by the
 * browser's or the app's back never drops what was typed.
 *
 * CONEK-8857 — every page form also carries the screen it comes from: a token
 * minted when it was rendered, the revision rendered, and `answers_seq`, when
 * the page was last edited here, in ms of the device's clock (0 until it is).
 * Inspections::SavePage orders saves of one screen by it: an offline-queue
 * replay (bridge--offline-queue, on the phone) or a background save that its
 * screen already sent a later edit of writes no answer, so a save given up on
 * (a timeout) that the server still finishes, a late retry or a flush on
 * leave never undoes a newer one.
 *
 * Offline continuity, in deck mode:
 * every page's answers are also written to the device (DraftStore,
 * IndexedDB) as they are given, with the screen they were given on, and
 * removed once the server confirms a save of them. When the pager loads,
 * whatever is still there — the connection dropped and the app was closed,
 * the tab reloaded — is put back into its page, which becomes that screen
 * again, and sent as a replay of it: Inspections::SavePage applies it unless
 * the page was changed elsewhere since, and then the page goes back to what
 * the server rendered. Restoring is not an edit: it does not move the seq.
 * An edit of a restored page is: from then on its answers are the
 * inspector's, saved as an edit of theirs like any other, which a change
 * made elsewhere does not undo.
 *
 * It is a time rather than a counter because the same screen can come back
 * as an older copy: Hotwire Native restores a page from its snapshot, from
 * before later edits and saves. A counter would start again from that copy's
 * lower value and its next edit could look older than what is already saved;
 * the clock is past all of it. It never goes down.
 *
 * Per the repo "no disabled submit buttons" rule the buttons stay clickable;
 * they are marked busy only while a classic submit is in flight.
 */
export default class extends Controller {
  static targets = [
    "direction",
    "submit",
    "slide",
    "form",
    "progressStack",
    "status",
    "header",
  ]

  static values = {
    deck: Boolean,
    savingLabel: String,
    savedLabel: String,
    retryingLabel: String,
    offlineLabel: String,
    offlineKeptLabel: String,
    failedLabel: String,
    stoppedLabel: String,
    signedOutLabel: String,
    owner: String,
    inspection: String,
  }

  connect() {
    // Both modes: every edit moves its page's `answers_seq` forward.
    this.onInput = (event) => this.onEdit(event)
    this.element.addEventListener("input", this.onInput)
    this.element.addEventListener("change", this.onInput)
    if (!this.deckValue) return

    this.dirtyPages = new Set()
    this.leftPages = new Set()
    this.replayPages = new Set()
    this.rendered = new Map()
    this.revisions = new Map()
    this.draftTimers = new Map()
    this.queue = new PageSaveQueue({
      onState: (state) => this.renderStatus(state),
      activePageId: () => this.activePageId,
      onSend: (pageId) => this.revisions.get(pageId) || 0,
      onSaved: (pageId, revision) => this.draftLanded(pageId, revision),
      onSuperseded: (pageId) => this.draftSuperseded(pageId),
    })

    this.onDeckChange = (event) => this.deckChanged(event)
    this.onDeckOverflow = (event) => this.deckOverflowed(event)
    this.onVisibility = () => {
      if (document.visibilityState === "hidden") this.flushOnLeave()
    }
    this.onLeave = () => this.flushOnLeave()
    this.onStreamRender = (event) => this.beforeStreamRender(event)

    this.element.addEventListener("ui--swipe-deck:change", this.onDeckChange)
    this.element.addEventListener(
      "ui--swipe-deck:overflow",
      this.onDeckOverflow,
    )
    document.addEventListener("visibilitychange", this.onVisibility)
    window.addEventListener("pagehide", this.onLeave)
    document.addEventListener("turbo:visit", this.onLeave)
    document.addEventListener("turbo:before-stream-render", this.onStreamRender)

    this.loadNeighbourImages(this.activeIndex)
    this.openDrafts()
  }

  disconnect() {
    this.element.removeEventListener("input", this.onInput)
    this.element.removeEventListener("change", this.onInput)
    if (!this.queue) return
    this.flushOnLeave()
    this.draftTimers.forEach((timer) => clearTimeout(timer))
    this.drafts?.close()
    this.drafts = null
    this.element.removeEventListener("ui--swipe-deck:change", this.onDeckChange)
    this.element.removeEventListener(
      "ui--swipe-deck:overflow",
      this.onDeckOverflow,
    )
    document.removeEventListener("visibilitychange", this.onVisibility)
    window.removeEventListener("pagehide", this.onLeave)
    document.removeEventListener("turbo:visit", this.onLeave)
    document.removeEventListener(
      "turbo:before-stream-render",
      this.onStreamRender,
    )
    this.queue.destroy()
    this.queue = null
  }

  // A stack of per-page chrome (the progress bars, the web pager's pills and
  // step rail) connected — at load, or because a save's Turbo Stream replaced
  // it: show the entry of the page on screen now, which may be past the page
  // that was saved, and give focus back to the step that had it (see
  // beforeStreamRender).
  progressStackTargetConnected(stack) {
    if (!this.deckValue || !this.activePageId) return
    this.showProgressFor(this.activePageId)
    if (this.stackFocus?.stackId === stack.id) {
      const step = this.visibleEntry(stack)?.querySelector(
        `[data-testid="${this.stackFocus.step}"]`,
      )
      this.stackFocus = null
      if (step instanceof HTMLButtonElement) step.focus()
      else this.focusSlide(this.slideTargets[this.activeIndex])
    }
  }

  // A stack is about to be replaced: remember which step had the keyboard, or
  // focus would fall to the document body with the old stack.
  beforeStreamRender(event) {
    const target = event.target?.getAttribute?.("target")
    const stack = this.progressStackTargets.find(
      (candidate) => candidate.id && candidate.id === target,
    )
    if (!stack) return
    const step = this.focusedStep(stack)
    if (step) this.stackFocus = { stackId: stack.id, step }
  }

  // ── buttons ────────────────────────────────────────────────────────────

  goNext(event) {
    this.setDirection(event, "next")
  }

  goBack(event) {
    this.setDirection(event, "back")
  }

  goReview(event) {
    this.setDirection(event, "review")
  }

  // The footer buttons reach their page's form through `form=`; the hidden
  // field to set is that form's, not the first one in the document.
  setDirection(event, value) {
    const field = this.directionField(event?.currentTarget?.form)
    if (field) field.value = value
  }

  // CONEK-8805 — the web footer buttons post their own `direction` too (so
  // the pager navigates without JS), which makes
  // `form.elements.namedItem("direction")` a RadioNodeList whose value is
  // "" and whose setter does nothing. The hidden field is the one to use.
  directionField(form) {
    return form?.querySelector(
      "input[type=hidden][data-inspections--pager-target='direction']",
    )
  }

  // The pressed button's own direction when it carries one, else the hidden
  // field's.
  directionOf(form, submitter) {
    if (submitter?.name === "direction" && submitter.value) {
      return submitter.value
    }
    return this.directionField(form)?.value
  }

  onSubmit(event) {
    if (this.bypassNextSubmit) {
      this.bypassNextSubmit = false
      this.reviewSubmitted = true
      this.markBusy()
      return
    }
    if (!this.deckValue || !this.deck) {
      this.markBusy()
      return
    }

    event.preventDefault()
    const form = event.target
    const submitter = event.submitter

    if (submitter?.name === "target_page_id") {
      if (submitter.value === "review") {
        this.openReview(form, submitter)
      } else {
        const index = this.indexOfPage(submitter.value)
        if (index >= 0) this.deck.goTo(index)
      }
      return
    }

    const direction = this.directionOf(form, submitter)
    if (direction === "back") this.deck.previous()
    else if (direction === "review") this.openReview(form, submitter)
    else this.deck.next() // past the last page the deck reports an overflow: review
  }

  markBusy() {
    this.submitTargets.forEach((button) =>
      button.setAttribute("aria-busy", "true"),
    )
  }

  // A classic submit ends in a new page, which brings fresh buttons. One that
  // does not — refused, or no connection — leaves this page in place, so its
  // buttons must not stay marked busy.
  submitEnded(event) {
    if (event.detail?.success) return
    this.submitTargets.forEach((button) => button.removeAttribute("aria-busy"))
  }

  // CONEK-8857 — on the phone pager an offline submit is handed to the native
  // queue (`bridge--offline-queue`) instead of being sent, so no navigation
  // follows and the page stays on screen. The buttons are not busy any more.
  onQueued() {
    this.submitTargets.forEach((button) => button.removeAttribute("aria-busy"))
  }

  // ── the deck ───────────────────────────────────────────────────────────

  deckChanged(event) {
    const { from, to } = event.detail
    const left = this.slideTargets[from]
    const shown = this.slideTargets[to]
    if (!left || !shown) return

    // A jump from a progress step hides the bar holding the focused step: the
    // keyboard follows the inspector to the page they jumped to.
    const stepHadFocus = Boolean(this.focusedStep())
    this.saveSlide(left, { complete: true })
    this.showProgressFor(shown.dataset.pageId)
    if (stepHadFocus) this.focusSlide(shown)
    this.scrollToTop()
    this.followTitle(shown)
    this.followLocation(shown)
    this.loadNeighbourImages(to)
  }

  deckOverflowed(event) {
    if (event.detail?.direction !== "next") return
    const form = this.formOf(this.slideTargets[this.activeIndex])
    if (form) this.openReview(form, null)
  }

  // Held until the review's submit has started, so a second tap or a swipe
  // past the last page meanwhile cannot submit it twice.
  async openReview(form, submitter) {
    if (this.openingReview) return
    this.openingReview = true
    try {
      // The page on screen is saved by the submit itself, then the server
      // redirects to the review — so its own refusal does not count here.
      const pageId = this.pageIdOf(form)
      const result = await this.queue.drain({ except: pageId })
      if (!result.ok) {
        // Stay on the deck: the status line says why. A refused page is shown,
        // so the inspector can see it before trying again.
        if (result.failedPageId) {
          const index = this.indexOfPage(result.failedPageId)
          if (index >= 0 && index !== this.activeIndex) this.deck.goTo(index)
        }
        this.renderStatus(result.state)
        return
      }

      // In a task of its own: with nothing left to save, the drain above
      // resolves in a microtask that can run while the browser is still
      // firing the submit that got us here, and the HTML spec ignores a
      // requestSubmit made during that ("firing submission events") — the
      // Review button would silently do nothing.
      await new Promise((resolve) => setTimeout(resolve, 0))
      if (!form.isConnected) return
      this.submitReview(form, submitter, pageId)
    } finally {
      this.openingReview = false
    }
  }

  // requestSubmit fires the submit event before it returns; a form the
  // browser refuses to submit (an out-of-range number still focused) fires
  // none. So the bypass never outlives the call — the next Back / Next would
  // otherwise navigate — and the page stays dirty unless the submit started.
  submitReview(form, submitter, pageId) {
    const field = this.directionField(form)
    const previous = field?.value
    if (field) field.value = "review"
    this.reviewSubmitted = false
    this.bypassNextSubmit = true
    if (submitter && submitter.form === form) form.requestSubmit(submitter)
    else form.requestSubmit()
    this.bypassNextSubmit = false
    if (this.reviewSubmitted) this.dirtyPages.delete(pageId)
    else if (field) field.value = previous
  }

  // ── saving ─────────────────────────────────────────────────────────────

  saveSlide(slide, { complete }) {
    const pageId = slide.dataset.pageId
    const form = this.formOf(slide)
    if (!pageId || !form) return
    if (complete) this.leftPages.add(pageId)
    this.dirtyPages.delete(pageId)
    this.writeDraftNow(pageId)
    this.queue.enqueue(pageId, form, {
      complete,
      replay: this.replayPages.has(pageId),
    })
  }

  // An answer changed: its page's `answers_seq` moves forward and, in the
  // deck, the page is saved when it is left. A photo is uploaded on its own
  // (inspections--photo-grid, its own form); it is not an answer.
  onEdit(event) {
    // A draft being put back is not an edit made here.
    if (this.restoring) return
    const form = event.target?.form
    const seq = form?.elements.namedItem("answers_seq")
    if (!seq) return
    // A hidden input's value IS its attribute, so a copy of the page Hotwire
    // Native restores carries it too.
    seq.value = String(
      Math.max(Date.now(), (Number.parseInt(seq.value, 10) || 0) + 1),
    )
    const pageId = this.pageIdOf(form)
    if (!this.dirtyPages || !pageId) return
    this.dirtyPages.add(pageId)
    // A restored page the inspector works on: its answers are theirs now.
    this.replayPages.delete(pageId)
    this.rendered.delete(pageId)
    this.scheduleDraft(pageId)
  }

  // The document is hidden or going away: queue the page being edited without
  // completing it (the inspector has not left it), then hand everything still
  // queued to the browser with keepalive.
  flushOnLeave() {
    if (!this.queue) return
    // Best effort: IndexedDB writes are asynchronous and may not finish if the
    // document is being destroyed, which is why the keepalive saves follow.
    Array.from(this.draftTimers.keys()).forEach((pageId) =>
      this.writeDraftNow(pageId),
    )
    const slide = this.slideTargets[this.activeIndex]
    if (slide && this.dirtyPages.has(slide.dataset.pageId)) {
      this.dirtyPages.delete(slide.dataset.pageId)
      this.queue.hold(slide.dataset.pageId, this.formOf(slide), {
        complete: false,
        replay: this.replayPages.has(slide.dataset.pageId),
      })
    }
    this.queue.flushOnLeave()
  }

  // ── offline drafts ─────────────────────────────────────────────────────

  async openDrafts() {
    const drafts = await DraftStore.open()
    if (!drafts) return
    if (!this.queue) {
      drafts.close()
      return
    }
    this.drafts = drafts
    // Ask the browser not to evict the drafts under storage pressure (Safari
    // drops script-written storage after seven days without a visit).
    navigator.storage?.persist?.().catch(() => undefined)
    // Drafts of inspections never opened again would stay forever.
    drafts.purgeOlderThan(Date.now() - DRAFT_MAX_AGE_MS)

    const records = await drafts.forInspection(
      this.ownerValue,
      this.inspectionValue,
    )
    // The pager may have gone meanwhile; the drafts wait for the next one.
    if (!this.queue) return
    records.forEach((record) => {
      // One unreadable draft must not keep the others from coming back.
      try {
        this.restoreDraft(record)
      } catch {
        // left on the device until it is purged
      }
    })
  }

  // The page becomes the screen the draft was given on, shows its answers,
  // and is sent as a replay of that screen. What the server rendered is kept,
  // to go back to if the server holds a newer change.
  restoreDraft(record) {
    const form = this.formOf(this.slideTargets[this.indexOfPage(record.pageId)])
    if (!form || !record.screen || !Array.isArray(record.fields)) return
    this.rendered.set(record.pageId, {
      fields: serializeAnswers(form),
      screen: readScreen(form),
    })
    this.putBack(form, record.screen, record.fields)
    this.revisions.set(record.pageId, record.rev)
    this.replayPages.add(record.pageId)
    if (record.complete) this.leftPages.add(record.pageId)
    this.cancelDraftTimer(record.pageId)
    this.queue.enqueue(record.pageId, form, {
      complete: Boolean(record.complete),
      replay: true,
    })
  }

  putBack(form, screen, fields) {
    this.restoring = true
    try {
      writeScreen(form, screen)
      restoreAnswers(form, fields)
    } finally {
      this.restoring = false
    }
  }

  scheduleDraft(pageId) {
    this.cancelDraftTimer(pageId)
    this.draftTimers.set(
      pageId,
      setTimeout(() => this.writeDraftNow(pageId), 300),
    )
  }

  cancelDraftTimer(pageId) {
    clearTimeout(this.draftTimers.get(pageId))
    this.draftTimers.delete(pageId)
  }

  writeDraftNow(pageId) {
    this.cancelDraftTimer(pageId)
    if (!this.drafts) return
    const form = this.formOf(this.slideTargets[this.indexOfPage(pageId)])
    if (!form) return
    const revision = (this.revisions.get(pageId) || 0) + 1
    this.revisions.set(pageId, revision)
    this.drafts.put({
      userId: this.ownerValue,
      inspectionId: this.inspectionValue,
      pageId,
      fields: serializeAnswers(form),
      screen: readScreen(form),
      complete: this.leftPages.has(pageId),
      rev: revision,
      updatedAt: Date.now(),
    })
  }

  // A save of the page landed: it is no longer a replay, and its draft goes,
  // unless the page was edited again after that save was sent.
  draftLanded(pageId, revision) {
    this.replayPages.delete(pageId)
    this.rendered.delete(pageId)
    this.dropDraft(pageId, revision)
  }

  // The server holds a newer change of the page than the draft: the page goes
  // back to what the server rendered (its answers and its own screen), and a
  // page the inspector had left is still completed, with those answers. A
  // page edited since it was put back keeps its answers: they are going out
  // as the inspector's edit, and the draft stays until that save lands.
  draftSuperseded(pageId) {
    if (!this.replayPages.has(pageId)) return
    this.replayPages.delete(pageId)
    this.cancelDraftTimer(pageId)
    this.dirtyPages.delete(pageId)
    this.dropDraft(pageId, Number.MAX_SAFE_INTEGER)
    const rendered = this.rendered.get(pageId)
    this.rendered.delete(pageId)
    const form = this.formOf(this.slideTargets[this.indexOfPage(pageId)])
    if (!rendered || !form) return
    this.putBack(form, rendered.screen, rendered.fields)
    if (this.leftPages.has(pageId))
      this.queue.enqueue(pageId, form, { complete: true })
  }

  dropDraft(pageId, revision) {
    this.drafts?.deleteUpTo(
      this.ownerValue,
      this.inspectionValue,
      pageId,
      revision,
    )
  }

  // Offline, the notice says where the unsent answers are: on the device when
  // the drafts are open, otherwise only in this page (no IndexedDB, or offline
  // capture off) — then it must not be closed.
  renderStatus(state) {
    if (!this.hasStatusTarget) return
    const labels = {
      saving: this.savingLabelValue,
      saved: this.savedLabelValue,
      retrying: this.retryingLabelValue,
      offline: this.drafts
        ? this.offlineKeptLabelValue
        : this.offlineLabelValue,
      failed: this.failedLabelValue,
      stopped: this.stoppedLabelValue,
      "signed-out": this.signedOutLabelValue,
    }
    this.statusTarget.dataset.state = state
    this.statusTarget.textContent = labels[state] || ""
  }

  // ── following the active page ──────────────────────────────────────────

  showProgressFor(pageId) {
    this.progressStackTargets.forEach((stack) => {
      Array.from(stack.children).forEach((entry) => {
        entry.hidden = entry.dataset.pageId !== pageId
      })
    })
  }

  visibleEntry(stack) {
    return Array.from(stack.children).find((entry) => !entry.hidden) || null
  }

  // The test id of the progress step holding the keyboard, if one does — in
  // `stack`, or in any stack.
  focusedStep(stack = null) {
    const focused = document.activeElement
    if (!focused) return null
    const stacks = stack ? [stack] : this.progressStackTargets
    if (!stacks.some((candidate) => candidate.contains(focused))) return null
    return focused.closest("[data-testid]")?.dataset.testid || null
  }

  // The web pager's header names the page on screen (CONEK-7591); the phone
  // view's title is the inspection's and has no header target.
  followTitle(slide) {
    if (!this.hasHeaderTarget || !slide.dataset.pageTitle) return
    const title = this.headerTarget.querySelector("h1")
    if (title) title.textContent = slide.dataset.pageTitle
  }

  // The page moved to starts at its top, as a page navigated to does: a Next
  // tapped at the foot of a long page must not land halfway down the next
  // one, under the pinned header. Whatever scrolls the pager — the window, or
  // a scrolling ancestor in another layout — goes back to the top.
  scrollToTop() {
    for (
      let node = this.element.parentElement;
      node;
      node = node.parentElement
    ) {
      if (node.scrollTop > 0) node.scrollTo({ top: 0, behavior: "instant" })
    }
    if (window.scrollY > 0) window.scrollTo({ top: 0, behavior: "instant" })
  }

  // The deck's slide (its <section>, focusable with tabindex=-1) around a
  // page's slide target.
  focusSlide(slide) {
    slide?.closest("section")?.focus({ preventScroll: true })
  }

  // Replace, not push: Back leaves the pager rather than stepping through
  // every page visited. Through Turbo's own history so its restoration state
  // stays consistent (prior art: prospecting--map).
  followLocation(slide) {
    const url = slide.dataset.pageUrl
    if (url) {
      const turbo = window.Turbo?.navigator
      if (turbo?.history && turbo.view) {
        turbo.history.replace(
          new URL(url, window.location.href),
          turbo.history.restorationIdentifier,
        )
        turbo.view.lastRenderedLocation = new URL(url, window.location.href)
      } else {
        window.history.replaceState(window.history.state, "", url)
      }
    }
    if (slide.dataset.documentTitle)
      document.title = slide.dataset.documentTitle
  }

  // Hidden slides' lazy images never load; load the neighbours' up front so a
  // swipe shows the next diagram, not an empty box.
  loadNeighbourImages(index) {
    ;[index - 1, index + 1].forEach((i) => {
      this.slideTargets[i]
        ?.querySelectorAll('img[loading="lazy"]')
        .forEach((img) => {
          img.loading = "eager"
        })
    })
  }

  // ── lookups ────────────────────────────────────────────────────────────

  get deckElement() {
    return this.element.querySelector('[data-controller~="ui--swipe-deck"]')
  }

  get deck() {
    const element = this.deckElement
    return element
      ? this.application.getControllerForElementAndIdentifier(
          element,
          "ui--swipe-deck",
        )
      : null
  }

  // From the deck's index value, not its controller: Stimulus connects this
  // outer controller (and its targets) before the deck inside it, and the
  // attribute is there from the server's first paint — the page the inspector
  // opened, not the first one. The deck keeps the attribute current.
  get activeIndex() {
    const index = Number(
      this.deckElement?.getAttribute("data-ui--swipe-deck-index-value"),
    )
    return Number.isInteger(index) ? index : 0
  }

  get activePageId() {
    return this.slideTargets[this.activeIndex]?.dataset.pageId || null
  }

  indexOfPage(pageId) {
    return this.slideTargets.findIndex(
      (slide) => slide.dataset.pageId === pageId,
    )
  }

  formOf(slide) {
    return (
      slide?.querySelector('form[data-inspections--pager-target~="form"]') ||
      null
    )
  }

  pageIdOf(form) {
    return form.closest('[data-inspections--pager-target~="slide"]')?.dataset
      .pageId
  }
}
